import { env } from '@skin-seoul/env-utils';

import axios, { AxiosAdapter, AxiosInstance } from 'axios';
import { attachServerLoggingInterceptor } from './loggingInterceptor';
import { getServiceLogger } from './serviceLogger';

const DEFAULT_EVENT_URL = 'https://dev-event.skin-seoul.com';

let hasWarnedMissingEnv = false;

const getEventBaseURL = (): string => {
  const url = env('NEXT_PUBLIC_ENV_EVENT_ORIGIN');
  if (!url && process.env.NODE_ENV !== 'development' && !hasWarnedMissingEnv) {
    hasWarnedMissingEnv = true;
    getServiceLogger().warn('[analyticsClient] NEXT_PUBLIC_ENV_EVENT_ORIGIN is not set, falling back to dev URL');
  }
  return url || DEFAULT_EVENT_URL;
};

const getHmacSecret = (): string | undefined => {
  return env('NEXT_PUBLIC_ANALYTICS_HMAC_SECRET') || process.env.EXPO_PUBLIC_ANALYTICS_HMAC_SECRET || undefined;
};

const signRequest = async (body: string, secretOverride?: string): Promise<string | undefined> => {
  const secret = secretOverride ?? getHmacSecret();
  if (!secret) return undefined;

  const timestamp = Date.now().toString();
  const payload = `${timestamp}.${body}`;

  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
      'sign',
    ]);
    const signatureBuffer = await crypto.subtle.sign('HMAC', key, encoder.encode(payload));
    const signature = Array.from(new Uint8Array(signatureBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `${timestamp}:${signature}`;
  }

  // React Native / Node.js fallback using crypto-js. Loaded on demand: browsers take the
  // Web Crypto path above, so the 63KB library must not ship in the web bundle (SSD-6268).
  try {
    const { default: CryptoJS } = await import('crypto-js');
    const signature = CryptoJS.HmacSHA256(payload, secret).toString(CryptoJS.enc.Hex);
    return `${timestamp}:${signature}`;
  } catch {
    // Final fallback: Node.js crypto
    try {
      const { createHmac } = await import('crypto');
      const signature = createHmac('sha256', secret).update(payload).digest('hex');
      return `${timestamp}:${signature}`;
    } catch {
      return undefined;
    }
  }
};

// Browsers fail a keepalive fetch outright once in-flight keepalive bodies pass 64KiB (Chromium: TypeError),
// so events past this budget go out as normal fetches. Limits follow Sentry's fetch transport.
const KEEPALIVE_MAX_PENDING_BYTES = 60_000;
const KEEPALIVE_MAX_PENDING_REQUESTS = 15;
let keepalivePendingBytes = 0;
let keepalivePendingRequests = 0;

// keepalive lets an event already in flight finish when the visitor leaves the page (SSD-6666).
// ponytail: the budget cannot see keepalive bodies other scripts (GA4 beacons) hold; retry without keepalive if drops are measured.
const keepaliveFetchAdapter: AxiosAdapter = (config) => {
  const fetchAdapter = axios.getAdapter('fetch');
  const bodyBytes = typeof config.data === 'string' ? new TextEncoder().encode(config.data).length : 0;
  const fitsKeepaliveBudget =
    keepalivePendingRequests < KEEPALIVE_MAX_PENDING_REQUESTS &&
    keepalivePendingBytes + bodyBytes <= KEEPALIVE_MAX_PENDING_BYTES;
  if (!fitsKeepaliveBudget) {
    return fetchAdapter(config);
  }

  keepalivePendingRequests += 1;
  keepalivePendingBytes += bodyBytes;
  config.fetchOptions = { ...config.fetchOptions, keepalive: true };
  return fetchAdapter(config).finally(() => {
    keepalivePendingRequests -= 1;
    keepalivePendingBytes -= bodyBytes;
  });
};

export const createSkinSeoulAnalyticsClient = (_baseURL?: string, _hmacSecret?: string): AxiosInstance => {
  const initialBaseURL = _baseURL ?? getEventBaseURL();

  const instance = axios.create({
    baseURL: initialBaseURL,
    withCredentials: true,
    adapter: keepaliveFetchAdapter,
  });

  instance.interceptors.request.use(async (config) => {
    // Dynamic baseURL update (runtime env may differ from build-time)
    if (typeof window !== 'undefined') {
      const currentBaseURL = getEventBaseURL();
      const configBaseURL = config.baseURL || instance.defaults.baseURL;

      if (currentBaseURL !== DEFAULT_EVENT_URL && configBaseURL !== currentBaseURL) {
        config.baseURL = currentBaseURL;
      }
    }

    // HMAC signature
    if (config.data) {
      const bodyJson = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);

      const signature = await signRequest(bodyJson, _hmacSecret);
      if (signature) {
        config.headers['X-Analytics-Signature'] = signature;
      }
    }

    return config;
  });

  attachServerLoggingInterceptor(instance, 'analyticsClient');

  return instance;
};

export let analyticsClient: AxiosInstance = createSkinSeoulAnalyticsClient();

export const setAnalyticsClient = (instance: AxiosInstance) => {
  analyticsClient = instance;
};
